import { z } from "zod";
export const stockMovementTypeSchema = z.enum([
    "ENTRY",
    "WITHDRAWAL",
    "RETURN",
    "TRANSFER",
    "ADJUSTMENT",
    "DISPOSAL"
]);
export const stockProductKindSchema = z.enum(["CONSUMABLE", "ASSET"]);
export const stockLocationTypeSchema = z.enum(["STOCK", "SECTOR", "CABINET", "SHELF", "POSITION"]);
export const stockReplenishmentFrequencySchema = z.enum(["WEEKLY", "BIWEEKLY", "MONTHLY", "CUSTOM"]);
const optionalText = (maximum) => z.string().trim().max(maximum).optional();
export const stockProductsQuerySchema = z.object({
    search: z.string().trim().max(160).default(""),
    category: z.string().trim().max(100).optional(),
    locationId: z.string().trim().max(160).optional(),
    status: z.enum(["NORMAL", "LOW", "OUT"]).optional(),
    includeInactive: z.enum(["true", "false"]).default("false")
});
const stockProductFieldsSchema = z.object({
    name: z.string().trim().min(2).max(180),
    category: z.string().trim().min(2).max(100),
    sku: z.string().trim().max(80).nullable().optional(),
    barcode: optionalText(120),
    description: z.string().trim().max(5_000).default(""),
    photoUrl: z.string().trim().max(2_000).refine((value) => !value || value.startsWith("/") || z.string().url().safeParse(value).success, "Informe uma URL válida.").optional(),
    unit: z.string().trim().min(1).max(40).default("unidade"),
    kind: stockProductKindSchema.default("CONSUMABLE"),
    initialQuantity: z.number().int().min(0).max(10_000_000).default(0),
    unitPrice: z.number().min(0).max(999_999_999.99).default(0),
    minimumStock: z.number().int().min(0).max(10_000_000).default(0),
    idealStock: z.number().int().min(0).max(10_000_000).default(0),
    locationId: optionalText(160),
    recurringFrequency: stockReplenishmentFrequencySchema.nullable().optional(),
    recurringIntervalDays: z.number().int().min(1).max(3650).nullable().optional(),
    nextReplenishmentAt: z.string().datetime().nullable().optional()
}).strict();
export const createStockProductSchema = stockProductFieldsSchema.refine((input) => input.idealStock >= input.minimumStock, {
    path: ["idealStock"],
    message: "O estoque ideal deve ser igual ou maior que o mínimo."
});
export const updateStockProductSchema = stockProductFieldsSchema
    .omit({ initialQuantity: true })
    .partial()
    .extend({ expectedVersion: z.number().int().min(0), isActive: z.boolean().optional() })
    .refine((input) => Object.keys(input).some((key) => key !== "expectedVersion"), "Informe ao menos uma alteração.");
export const createStockLocationSchema = z.object({
    name: z.string().trim().min(2).max(120),
    type: stockLocationTypeSchema,
    parentId: optionalText(160)
});
export const updateStockLocationSchema = z.object({
    name: z.string().trim().min(2).max(120).optional(),
    type: stockLocationTypeSchema.optional(),
    parentId: z.string().trim().min(1).max(160).nullable().optional()
}).refine((input) => Object.keys(input).length > 0, "Informe ao menos uma alteração.");
export const stockMovementItemInputSchema = z.object({
    productId: z.string().trim().min(1).max(160),
    quantity: z.number().int().min(1).max(10_000_000)
});
export const createStockWithdrawalSchema = z.object({
    companyId: z.string().trim().min(1).max(160),
    contactId: optionalText(160),
    description: z.string().trim().max(5_000).default(""),
    items: z.array(stockMovementItemInputSchema).min(1).max(100)
});
export const createStockEntrySchema = z.object({
    locationId: optionalText(160),
    description: z.string().trim().max(5_000).default(""),
    items: z.array(stockMovementItemInputSchema).min(1).max(100)
});
export const stockMovementsQuerySchema = z.object({
    search: z.string().trim().max(160).default(""),
    type: stockMovementTypeSchema.optional(),
    productId: optionalText(160),
    category: optionalText(100),
    companyId: optionalText(160),
    contactId: optionalText(160),
    responsibleUserId: optionalText(160),
    locationId: optionalText(160),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional()
});
export const addStockPurchaseListItemSchema = z.object({
    productId: z.string().trim().min(1).max(160),
    quantity: z.number().int().min(1).max(10_000_000),
    notes: z.string().trim().max(1_000).default("")
});
